import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import type { OpsActionRecord, OpsDecision, OpsQueueResponse, Resolution } from "./types";
import { POLL_INTERVAL_MS } from "./useOpsOverview";

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

/**
 * The decisions queue (GET /eve/v1/ops/queue) and the one write the board
 * makes: POST /eve/v1/ops/decisions/resolve. While amy runs with
 * OPS_WRITE_ACTIONS off, an Accept is RECORDED as would-do — the response's
 * action record says so and the list re-reads itself.
 */
export function useOpsQueue(enabled: boolean) {
  const { getToken } = useAuth();
  const [data, setData] = useState<OpsQueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<OpsActionRecord | null>(null);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const mySeq = ++seq.current;
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${EVE_HOST}/eve/v1/ops/queue`, { headers: { Authorization: `Bearer ${token ?? ""}` } });
      const body = (await response.json().catch(() => null)) as (OpsQueueResponse & { error?: string }) | null;
      if (mySeq !== seq.current) return;
      if (!response.ok || !body?.ok) setError(body?.error ?? `Could not reach Amy (${response.status})`);
      else {
        setData(body);
        setError(null);
      }
    } catch (err) {
      if (mySeq !== seq.current) return;
      setError(err instanceof Error ? err.message : "Could not reach Amy");
    } finally {
      if (mySeq === seq.current) setLoading(false);
    }
  }, [getToken]);

  const resolve = useCallback(
    async (decision: OpsDecision, resolution: Resolution, opts: { snoozeDays?: number; note?: string } = {}): Promise<string | null> => {
      setResolving(decision._id);
      try {
        const token = await getToken();
        const response = await fetch(`${EVE_HOST}/eve/v1/ops/decisions/resolve`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token ?? ""}`, "Content-Type": "application/json" },
          body: JSON.stringify({ id: decision._id, resolution, snooze_days: opts.snoozeDays, note: opts.note ?? null }),
        });
        const body = (await response.json().catch(() => null)) as { ok: boolean; error?: string; action?: OpsActionRecord | null } | null;
        if (!response.ok || !body?.ok) return body?.error ?? `Amy refused (${response.status})`;
        setLastAction(body.action ?? null);
        await refresh();
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : "Could not reach Amy";
      } finally {
        setResolving(null);
      }
    },
    [getToken, refresh]
  );

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, refresh]);

  return { data, error, loading, resolving, lastAction, refresh, resolve };
}
